import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { FadeUp, MaskedLine, Eyebrow, EASE_OUT } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";

const HERO_IMG = "https://images.unsplash.com/photo-1573167507387-6b4b98cb7c13?crop=entropy&cs=srgb&fm=jpg&q=85";
const FOUNDER_IMG = "https://customer-assets-wrfwihn1.emergentagent.net/job_executive-hire-pro/artifacts/mww4f41g_c.webp";
const OFFICE_IMG = "https://images.unsplash.com/photo-1541746972996-4e0b0f43e02a?crop=entropy&cs=srgb&fm=jpg&q=85";

const CLIENT_TYPES = [
  "Startups & Scale-ups", "Technology & SaaS", "Global Capability Centers",
  "Product Companies", "Internet Product", "InsurTech", "MarTech & AdTech",
  "Digital Businesses", "Financial Services", "Healthcare Technology",
  "Management Consulting", "Energy Sector", "Semiconductors",
  "FMCG & Manufacturing", "Established Enterprises",
];

const SERVICES_PREVIEW = [
  { num: "01", title: "Executive Search", desc: "CXO, business heads and leadership mandates built on business context, not keyword matching." },
  { num: "02", title: "Retained Search", desc: "Dedicated, confidential search partnership for mission-critical leadership positions." },
  { num: "03", title: "Permanent Hiring", desc: "Mid to senior hiring across technology, product and business functions — relevance over volume." },
  { num: "04", title: "Contract & Staff Augmentation", desc: "Flexible specialist talent for transformation programs, projects and rapid scaling." },
  { num: "05", title: "HR Advisory", desc: "HR strategy, organization design, compensation and talent frameworks for growing businesses." },
  { num: "06", title: "Training & Leadership", desc: "Practical, consulting-led programs for recruiters, managers and leadership teams." },
];

const PROCESS = [
  ["Understand", "The business, role, team, challenges and success criteria."],
  ["Define", "The requirement translated into a structured talent mandate."],
  ["Map", "Relevant talent pools, companies, industries and profiles."],
  ["Identify", "Market intelligence, networks, sourcing and RoleSense™."],
  ["Assess", "Evaluation against role context — not keywords."],
  ["Engage", "Consultants personally engage and qualify candidates."],
  ["Present", "Relevant, contextualized candidate profiles."],
  ["Close", "Interviews, negotiations, offers and joining."],
  ["Follow Through", "The relationship continues beyond the placement."],
];

const INDUSTRIES = [
  { title: "Technology & SaaS", items: "Software Engineering · Cloud · Data · AI/ML · Cybersecurity · DevOps · Product · Architecture", span: "lg:col-span-7" },
  { title: "InsurTech", items: "P&C Insurance · Guidewire · Duck Creek · Majesco · EIS · Core Platforms", span: "lg:col-span-5" },
  { title: "MarTech & AdTech", items: "Digital Marketing Technology · Ad Platforms · Customer Data · Marketing Automation · Analytics", span: "lg:col-span-5" },
  { title: "Healthcare Technology", items: "Healthcare SaaS · Digital Health · Healthcare Platforms · Technology Leadership", span: "lg:col-span-7" },
];

const AUDIENCES = [
  {
    title: "For Hiring Managers",
    line: "Stop reviewing hundreds of resumes. Start reviewing the right candidates.",
    points: ["Fewer irrelevant profiles", "More contextual information", "Better candidate conversations", "Faster, higher-quality decisions"],
  },
  {
    title: "For Founders",
    line: "Build your team before it becomes your bottleneck.",
    points: ["Leadership & core team hiring", "Technology recruitment", "HR foundations & compensation strategy", "One partner from first hire to leadership team"],
  },
  {
    title: "For HR Leaders",
    line: "Extend your recruitment capability.",
    points: ["Difficult-to-fill & executive positions", "Market mapping & talent intelligence", "Contract staffing & volume support", "An extension of your talent organization"],
  },
];

const WHY = [
  ["Specialist Expertise", "Deep experience across technology, SaaS and specialized industry domains."],
  ["Executive-Level Access", "Strong networks across leadership and specialist talent markets."],
  ["Contextual Assessment", "Candidates evaluated against the business requirement, not just the JD."],
  ["Human + Technology", "RoleSense™ enhances our process with proprietary intelligence."],
  ["Market Intelligence", "Insight on talent availability, compensation and competitive hiring."],
  ["Relationship Driven", "Long-term partnerships, not transactional vendor relationships."],
  ["Outcome Focused", "The objective isn't to send resumes. It's to help you make the right hire."],
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yGhost = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden pt-36 lg:pt-44" data-testid="hero-section">
        <motion.span
          aria-hidden
          style={{ y: yGhost }}
          className="ghost-type pointer-events-none absolute -top-6 left-1/2 -translate-x-1/2 select-none font-serif text-[26vw] leading-none tracking-tight lg:text-[20vw]"
        >
          ALLY
        </motion.span>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid items-end gap-14 pb-20 lg:grid-cols-12 lg:pb-28">
            <div className="lg:col-span-7">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.15 }}>
                <Eyebrow data-testid="hero-eyebrow">Executive Search · Talent Strategy · Human Intelligence</Eyebrow>
              </motion.div>
              <h1 className="mt-6 font-serif text-4xl leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl" data-testid="hero-headline">
                <MaskedLine delay={0.25}>Where Human Expertise</MaskedLine>
                <MaskedLine delay={0.4}>Meets Recruitment</MaskedLine>
                <MaskedLine delay={0.55}>
                  <span className="italic text-gold-dark">Intelligence.</span>
                </MaskedLine>
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.85, ease: EASE_OUT }}
                className="mt-8 max-w-xl text-base leading-relaxed text-ink-muted sm:text-lg"
                data-testid="hero-subheadline"
              >
                Executive Search, specialist hiring and HR advisory — powered by
                experienced consultants and RoleSense™, our proprietary
                recruitment intelligence platform. Across India, Asia & EMEA.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1, ease: EASE_OUT }}
                className="mt-10 flex flex-wrap items-center gap-4"
              >
                <Link
                  to="/about-contact#contact"
                  data-testid="hero-partner-with-us-button"
                  className="group inline-flex items-center gap-2 border border-ink bg-ink px-7 py-3.5 text-sm font-semibold text-paper transition-colors duration-300 hover:bg-gold hover:border-gold hover:text-ink"
                >
                  Partner With Us
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-explore-services-button"
                  className="group inline-flex items-center gap-2 px-2 py-3.5 text-sm font-semibold text-ink link-sweep"
                >
                  Explore Our Services
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </motion.div>
            </div>
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.7, ease: EASE_OUT }}
                className="relative"
              >
                <motion.div style={{ y: yImg }} className="relative">
                  <div className="absolute -left-4 -top-4 h-full w-full border border-gold/50" aria-hidden />
                  <div className="relative overflow-hidden">
                    <img
                      src={HERO_IMG}
                      alt="Executive boardroom consultation"
                      className="aspect-[4/5] w-full object-cover"
                      data-testid="hero-image"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/30 to-transparent" />
                  </div>
                  <div className="absolute -bottom-6 -left-6 bg-ink px-6 py-5 text-paper shadow-2xl" data-testid="hero-badge">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Powered by</p>
                    <p className="mt-1 font-serif text-xl">RoleSense™</p>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* CHAPTER 01 — WHO WE ARE */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-36" data-testid="who-we-are-section">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <FadeUp>
              <Eyebrow>01 — Who We Are</Eyebrow>
              <h2 className="mt-5 font-serif text-2xl tracking-tight text-ink sm:text-3xl lg:text-4xl">
                Talent decisions require more than resumes.
              </h2>
            </FadeUp>
          </div>
          <div className="lg:col-span-8">
            <FadeUp delay={0.1}>
              <p className="text-base leading-relaxed text-ink-muted sm:text-lg">
                The right hire is not simply the person who matches the job
                description. It is the person who understands the business
                context, can operate within the organization's culture, has the
                right functional and technical depth — and can create
                measurable impact.
              </p>
            </FadeUp>
            <div className="mt-12 grid gap-px border border-line bg-line sm:grid-cols-2" data-testid="philosophy-grid">
              {[
                "Context before credentials.",
                "Quality before volume.",
                "Relationships before transactions.",
                "Outcomes before closures.",
              ].map((line, i) => (
                <FadeUp key={line} delay={0.08 * i} className="bg-paper px-8 py-8">
                  <p className="font-mono text-[11px] text-gold-dark">0{i + 1}</p>
                  <p className="mt-3 font-serif text-xl italic text-ink sm:text-2xl">{line}</p>
                </FadeUp>
              ))}
            </div>
            <FadeUp delay={0.15} className="mt-12">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink-faded">We work with</p>
              <div className="mt-5 flex flex-wrap gap-2.5" data-testid="client-types-list">
                {CLIENT_TYPES.map((c) => (
                  <span key={c} className="border border-line bg-paper-card px-4 py-2 text-xs font-medium text-ink-muted transition-colors duration-300 hover:border-gold hover:text-ink">
                    {c}
                  </span>
                ))}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="border-y border-line bg-paper-deep py-24 lg:py-36" data-testid="services-preview-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Eyebrow>02 — Our Services</Eyebrow>
              <h2 className="mt-5 max-w-xl font-serif text-2xl tracking-tight text-ink sm:text-3xl lg:text-4xl">
                Talent solutions built around your business.
              </h2>
            </div>
            <Link to="/services" className="group inline-flex items-center gap-2 text-sm font-semibold text-ink link-sweep" data-testid="services-preview-view-all-link">
              View all services <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
          <div className="mt-14 grid gap-px border border-line-medium bg-line-medium md:grid-cols-2 lg:grid-cols-3">
            {SERVICES_PREVIEW.map((s, i) => (
              <FadeUp key={s.num} delay={0.06 * i}>
                <Link
                  to="/services"
                  className="group flex h-full flex-col bg-paper p-8 transition-colors duration-500 hover:bg-ink"
                  data-testid={`service-preview-card-${s.num}`}
                >
                  <p className="font-mono text-xs text-gold-dark transition-colors duration-500 group-hover:text-gold">{s.num}</p>
                  <h3 className="mt-4 font-serif text-xl text-ink transition-colors duration-500 group-hover:text-paper sm:text-2xl">{s.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-muted transition-colors duration-500 group-hover:text-paper/60">{s.desc}</p>
                  <ArrowUpRight className="mt-6 h-5 w-5 text-ink-faded transition-all duration-500 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-gold" />
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ROLESENSE TEASER */}
      <section className="bg-ink py-24 text-paper lg:py-36" data-testid="rolesense-teaser-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid items-center gap-14 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <FadeUp>
                <Eyebrow className="text-gold">03 — Our Technology Advantage</Eyebrow>
                <h2 className="mt-5 font-serif text-2xl tracking-tight sm:text-3xl lg:text-4xl">
                  Meet RoleSense™
                </h2>
                <p className="mt-6 max-w-lg text-base leading-relaxed text-paper/60 sm:text-lg">
                  Recruitment intelligence built for better talent decisions.
                  RoleSense™ brings context, consistency and intelligence into
                  the recruitment process. It doesn't replace recruiters —{" "}
                  <span className="text-gold">it makes recruiters better.</span>
                </p>
                <Link
                  to="/rolesense"
                  className="group mt-9 inline-flex items-center gap-2 border border-gold px-7 py-3.5 text-sm font-semibold text-gold transition-colors duration-300 hover:bg-gold hover:text-ink"
                  data-testid="rolesense-teaser-explore-button"
                >
                  Explore RoleSense™
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </FadeUp>
            </div>
            <div className="lg:col-span-6">
              <FadeUp delay={0.12}>
                <div className="border border-paper/15 p-8 lg:p-10" data-testid="human-ai-ratio-card">
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold">The Human + AI Model</p>
                  <div className="mt-8 space-y-7">
                    <div>
                      <div className="flex items-baseline justify-between">
                        <p className="font-serif text-lg">Human Judgment</p>
                        <p className="font-serif text-3xl text-gold">70%</p>
                      </div>
                      <div className="mt-3 h-px bg-paper/15">
                        <motion.div
                          className="h-px bg-gold"
                          initial={{ width: 0 }}
                          whileInView={{ width: "70%" }}
                          viewport={{ once: true }}
                          transition={{ duration: 1.4, ease: EASE_OUT }}
                        />
                      </div>
                      <p className="mt-3 text-xs text-paper/50">Judgment · Relationship · Motivation · Validation · Decision</p>
                    </div>
                    <div>
                      <div className="flex items-baseline justify-between">
                        <p className="font-serif text-lg">RoleSense™ Intelligence</p>
                        <p className="font-serif text-3xl text-paper/70">30%</p>
                      </div>
                      <div className="mt-3 h-px bg-paper/15">
                        <motion.div
                          className="h-px bg-paper/60"
                          initial={{ width: 0 }}
                          whileInView={{ width: "30%" }}
                          viewport={{ once: true }}
                          transition={{ duration: 1.4, delay: 0.2, ease: EASE_OUT }}
                        />
                      </div>
                      <p className="mt-3 text-xs text-paper/50">Extraction · Structuring · Comparison · Pattern Recognition</p>
                    </div>
                  </div>
                  <p className="mt-8 border-t border-paper/10 pt-6 text-sm italic text-paper/60">
                    Technology-assisted recruitment with human accountability.
                  </p>
                </div>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* HOW WE WORK */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-36" data-testid="process-section">
        <FadeUp>
          <Eyebrow>04 — How We Work</Eyebrow>
          <h2 className="mt-5 max-w-2xl font-serif text-2xl tracking-tight text-ink sm:text-3xl lg:text-4xl">
            Our recruitment intelligence process.
          </h2>
        </FadeUp>
        <div className="mt-14 grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
          {PROCESS.map(([title, desc], i) => (
            <FadeUp key={title} delay={0.05 * i} className="group bg-paper p-8 transition-colors duration-500 hover:bg-paper-deep">
              <p className="font-serif text-4xl text-line-medium transition-colors duration-500 group-hover:text-gold" aria-hidden>
                {String(i + 1).padStart(2, "0")}
              </p>
              <h3 className="mt-5 font-serif text-xl text-ink">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-muted">{desc}</p>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* INDUSTRIES BENTO */}
      <section className="border-y border-line bg-paper-deep py-24 lg:py-36" data-testid="industries-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <Eyebrow>05 — Specialist Expertise</Eyebrow>
            <h2 className="mt-5 max-w-2xl font-serif text-2xl tracking-tight text-ink sm:text-3xl lg:text-4xl">
              Deep domain understanding matters.
            </h2>
          </FadeUp>
          <div className="mt-14 grid gap-5 lg:grid-cols-12">
            {INDUSTRIES.map((ind, i) => (
              <FadeUp key={ind.title} delay={0.07 * i} className={`${ind.span} group relative overflow-hidden border border-line-medium bg-paper p-9 transition-colors duration-500 hover:border-gold`}>
                <p className="font-mono text-[11px] text-gold-dark">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="mt-4 font-serif text-xl text-ink sm:text-2xl">{ind.title}</h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-ink-muted">{ind.items}</p>
                <span className="absolute -bottom-8 -right-4 font-serif text-[7rem] italic leading-none text-gold-soft transition-transform duration-700 group-hover:-translate-y-2" aria-hidden>
                  {String(i + 1).padStart(2, "0")}
                </span>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIENCES */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-36" data-testid="audiences-section">
        <FadeUp>
          <Eyebrow>06 — Who We Serve</Eyebrow>
          <h2 className="mt-5 max-w-2xl font-serif text-2xl tracking-tight text-ink sm:text-3xl lg:text-4xl">
            Built around the people who build businesses.
          </h2>
        </FadeUp>
        <div className="mt-14 grid gap-px border border-line bg-line lg:grid-cols-3">
          {AUDIENCES.map((a, i) => (
            <FadeUp key={a.title} delay={0.08 * i} className="bg-paper p-9">
              <h3 className="font-serif text-xl text-ink sm:text-2xl">{a.title}</h3>
              <p className="mt-3 font-serif text-base italic text-gold-dark">{a.line}</p>
              <ul className="mt-6 space-y-3">
                {a.points.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-ink-muted">
                    <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" />
                    {p}
                  </li>
                ))}
              </ul>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* WHY ALLY */}
      <section className="border-y border-line bg-paper-deep py-24 lg:py-36" data-testid="why-ally-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <Eyebrow>07 — Why Ally eXecutive HR</Eyebrow>
            <h2 className="mt-5 max-w-3xl font-serif text-2xl tracking-tight text-ink sm:text-3xl lg:text-4xl">
              Recruitment is our profession. Understanding business is our advantage.
            </h2>
          </FadeUp>
          <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {WHY.map(([title, desc], i) => (
              <FadeUp key={title} delay={0.06 * i} className="border-t-2 border-ink pt-6">
                <h3 className="font-serif text-lg text-ink">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-muted">{desc}</p>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.1} className="mt-16 border border-line-medium bg-paper px-8 py-10 lg:px-12">
            <p className="max-w-3xl font-serif text-xl italic leading-relaxed text-ink sm:text-2xl" data-testid="results-statement">
              "The measure of recruitment is not how many CVs we send. It is how
              relevant the candidates are — and how well the person performs
              after joining."
            </p>
          </FadeUp>
        </div>
      </section>

      {/* FOUNDER STRIP */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-36" data-testid="founder-strip-section">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <FadeUp>
              <div className="relative">
                <div className="absolute -right-4 -top-4 h-full w-full border border-gold/50" aria-hidden />
                <img src={FOUNDER_IMG} alt="Shan Kaul, Founder & Director" className="relative aspect-[4/5] w-full object-cover object-top" data-testid="founder-image" />
              </div>
            </FadeUp>
          </div>
          <div className="lg:col-span-8 lg:pl-8">
            <FadeUp delay={0.1}>
              <Eyebrow>08 — About the Founder</Eyebrow>
              <h2 className="mt-5 font-serif text-2xl tracking-tight text-ink sm:text-3xl lg:text-4xl">Shan Kaul</h2>
              <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-ink-faded">Founder & Director — Ally eXecutive HR</p>
              <p className="mt-7 max-w-2xl text-base leading-relaxed text-ink-muted sm:text-lg">
                With extensive experience in Talent Acquisition, Executive Search
                and HR Consulting, Shan founded Ally eXecutive HR with a simple
                belief: recruitment should be about understanding people and
                businesses — not simply matching keywords.
              </p>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-ink-muted">
                RoleSense™ represents the next evolution of that philosophy:
                years of recruitment experience combined with technology
                designed to make talent decisions more contextual and intelligent.
              </p>
              <Link to="/about-contact" className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-ink link-sweep" data-testid="founder-read-more-link">
                More about the firm <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-ink py-24 text-paper lg:py-32" data-testid="cta-section">
        <img src={OFFICE_IMG} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-15" />
        <div className="relative mx-auto max-w-7xl px-6 text-center lg:px-10">
          <FadeUp>
            <Eyebrow className="text-gold">Looking for the Right Talent?</Eyebrow>
            <h2 className="mx-auto mt-6 max-w-3xl font-serif text-3xl tracking-tight sm:text-4xl lg:text-5xl">
              Let's discuss what you are <span className="italic text-gold">trying to achieve.</span>
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-paper/60">
              Hiring a senior leader, building a technology team, scaling a
              startup or strengthening your HR function — tell us about your
              requirement.
            </p>
            <Link
              to="/about-contact#contact"
              className="group mt-10 inline-flex items-center gap-2 border border-gold bg-gold px-8 py-4 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-transparent hover:text-gold"
              data-testid="cta-start-conversation-button"
            >
              Start a Conversation
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
};

export default Home;
